import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSector } from '../contexts/SectorContext';
import { TimezoneSelect } from '../components/TimezoneSelect';
import Toast, { ToastContainer } from '../components/Toast';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  CalendarAppointmentType,
  CalendarEvent,
  CalendarSettings,
  CalendarSlot,
  CalendarWorkingHours,
  cancelCalendarEvent,
  createCalendarEvent,
  recordCalendarOutcome,
  getCalendarHours,
  getCalendarSettings,
  getCalendarTypes,
  listCalendarEvents,
  listCalendarSlots,
  saveCalendarHours,
  saveCalendarSettings,
  saveCalendarType,
  updateCalendarEvent,
} from '../services/CalendarService';
import './AgendaPage.css';

dayjs.extend(customParseFormat);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const emptyType = (): CalendarAppointmentType => ({
  id: 0,
  name: '',
  durationMinutes: 60,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 15,
  isDefault: false,
});

const AgendaPage: React.FC = () => {
  const { selectedSectorId } = useSector();
  // Link do Hoje (HJ-05): /agenda?date=AAAA-MM-DD&eventId=N abre o dia e o evento.
  const [searchParams] = useSearchParams();
  const linkedDate = searchParams.get('date');
  const linkedEventId = Number(searchParams.get('eventId')) || null;
  const linkedEventOpened = useRef(false);
  const [day, setDay] = useState(
    linkedDate && dayjs(linkedDate, 'YYYY-MM-DD', true).isValid() ? linkedDate : dayjs().format('YYYY-MM-DD'),
  );
  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [hours, setHours] = useState<CalendarWorkingHours[]>([]);
  const [types, setTypes] = useState<CalendarAppointmentType[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<'event' | 'rules'>('event');
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('09:00');
  const [endsAt, setEndsAt] = useState('10:00');
  const [typeDraft, setTypeDraft] = useState<CalendarAppointmentType>(emptyType());
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);

  const zone = settings?.timeZoneId || 'America/Sao_Paulo';
  const defaultTypeId = useMemo(
    () => types.find((t) => t.isDefault)?.id ?? types[0]?.id ?? null,
    [types]
  );

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts((current) => [...current, { id, message, type }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 3000);
  };

  const range = useCallback(() => {
    const start = dayjs.tz(day, zone).startOf('day');
    return { from: start.utc().toISOString(), to: start.add(1, 'day').utc().toISOString() };
  }, [day, zone]);

  const loadDay = useCallback(async (sectorId: number, typeId: number | null) => {
    const { from, to } = range();
    const [dayEvents, daySlots] = await Promise.all([
      listCalendarEvents(sectorId, from, to),
      listCalendarSlots(sectorId, typeId, from, to).catch(() => [] as CalendarSlot[]),
    ]);
    setEvents(dayEvents.filter((event) => event.status !== 'cancelled'));
    setSlots(daySlots);
  }, [range]);

  useEffect(() => {
    if (!selectedSectorId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [nextSettings, nextHours, nextTypes] = await Promise.all([
          getCalendarSettings(selectedSectorId),
          getCalendarHours(selectedSectorId),
          getCalendarTypes(selectedSectorId),
        ]);
        if (cancelled) return;
        setSettings(nextSettings);
        setHours(fullWeek(nextHours));
        setTypes(nextTypes);
      } catch (e) {
        if (!cancelled) setError(messageOf(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSectorId]);

  useEffect(() => {
    if (!selectedSectorId || !settings) return;
    void loadDay(selectedSectorId, defaultTypeId).catch((e) => setError(messageOf(e)));
  }, [selectedSectorId, settings, defaultTypeId, loadDay]);

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setDescription('');
    setLocation('');
    setStartsAt('09:00');
    setEndsAt('10:00');
  };

  const openEvent = (event: CalendarEvent) => {
    setEditing(event);
    setTitle(event.title);
    setDescription(event.description ?? '');
    setLocation(event.location ?? '');
    setStartsAt(dayjs.utc(event.startsAt).tz(zone).format('HH:mm'));
    setEndsAt(dayjs.utc(event.endsAt).tz(zone).format('HH:mm'));
    setPanel('event');
  };

  useEffect(() => {
    if (!linkedEventId || linkedEventOpened.current) return;
    const target = events.find((event) => event.id === linkedEventId);
    if (!target) return;
    linkedEventOpened.current = true;
    openEvent(target);
    // openEvent só lê o evento e o fuso; reabrir a cada render desfaria a edição do usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, linkedEventId]);

  const saveEvent = async () => {
    if (!selectedSectorId || !title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startsAt: dayjs.tz(`${day} ${startsAt}`, zone).utc().toISOString(),
        endsAt: dayjs.tz(`${day} ${endsAt}`, zone).utc().toISOString(),
        appointmentTypeId: defaultTypeId,
      };
      if (editing) await updateCalendarEvent(selectedSectorId, editing.id, body);
      else await createCalendarEvent(selectedSectorId, body);
      resetForm();
      await loadDay(selectedSectorId, defaultTypeId);
      addToast(editing ? 'Compromisso atualizado.' : 'Compromisso criado.', 'success');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const markOutcome = async (
    event: CalendarEvent,
    occurrence: 'occurred' | 'no_show',
    outcome: 'none' | 'no_decision' | 'closed' | 'lost'
  ) => {
    if (!selectedSectorId) return;
    setSaving(true);
    setError(null);
    try {
      await recordCalendarOutcome(selectedSectorId, event.id, { occurrence, outcome });
      await loadDay(selectedSectorId, defaultTypeId);
      addToast('Resultado registrado.', 'success');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelEvent = async (event: CalendarEvent) => {
    if (!selectedSectorId) return;
    setSaving(true);
    try {
      await cancelCalendarEvent(selectedSectorId, event.id);
      if (editing?.id === event.id) resetForm();
      await loadDay(selectedSectorId, defaultTypeId);
      addToast('Compromisso cancelado.', 'success');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const saveRules = async () => {
    if (!selectedSectorId || !settings) return;
    setSaving(true);
    setError(null);
    try {
      const next = await saveCalendarSettings({ ...settings, sectorId: selectedSectorId });
      setSettings(next);
      setHours(fullWeek(await saveCalendarHours(selectedSectorId, hours)));
      addToast('Parâmetros da agenda salvos.', 'success');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const saveType = async () => {
    if (!selectedSectorId || !typeDraft.name.trim()) return;
    setSaving(true);
    try {
      await saveCalendarType(selectedSectorId, { ...typeDraft, name: typeDraft.name.trim() });
      setTypes(await getCalendarTypes(selectedSectorId));
      setTypeDraft(emptyType());
      addToast('Tipo de compromisso salvo.', 'success');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedSectorId) {
    return <div className="agenda-page"><p className="agenda-empty">Selecione um setor para ver a agenda.</p></div>;
  }

  return (
    <div className="agenda-page">
      <div className="agenda-header">
        <div>
          <h1>Agenda</h1>
          <p className="agenda-event-meta">
            {`Fuso ${zone}. A sincronização com o Google Agenda é opcional.`}
          </p>
        </div>
        <div className="agenda-day-nav">
          <button type="button" onClick={() => setDay(dayjs(day).subtract(1, 'day').format('YYYY-MM-DD'))}>Anterior</button>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <button type="button" onClick={() => setDay(dayjs(day).add(1, 'day').format('YYYY-MM-DD'))}>Próximo</button>
        </div>
      </div>

      {error ? <p className="agenda-error">{error}</p> : null}
      {loading ? <LoadingSpinner size="md" color="primary" /> : null}

      <div className="agenda-layout">
        <section className="agenda-panel">
          <h2>{dayjs(day).format('DD/MM/YYYY')}</h2>
          {events.length === 0 ? <p className="agenda-empty">Nenhum compromisso neste dia.</p> : events.map((event) => (
            <article className="agenda-event" key={event.id}>
              <div className="agenda-event-main">
                <strong>{event.title}</strong>
                <div className="agenda-event-meta">
                  {dayjs.utc(event.startsAt).tz(zone).format('HH:mm')}–{dayjs.utc(event.endsAt).tz(zone).format('HH:mm')}
                  {event.source === 'google' ? ' · Google' : ''}
                  {event.location ? ` · ${event.location}` : ''}
                  {commercialLabel(event) ? ` · ${commercialLabel(event)}` : ''}
                </div>
                {event.status === 'confirmed' && !dayjs.utc(event.startsAt).isAfter(dayjs()) ? (
                  <div className="agenda-outcome-actions">
                    <button
                      type="button"
                      className={outcomeKey(event) === 'no_show' ? 'primary' : ''}
                      disabled={saving}
                      onClick={() => void markOutcome(event, 'no_show', 'none')}
                    >
                      Não compareceu
                    </button>
                    <button
                      type="button"
                      className={outcomeKey(event) === 'no_decision' ? 'primary' : ''}
                      disabled={saving}
                      onClick={() => void markOutcome(event, 'occurred', 'no_decision')}
                    >
                      Sem decisão
                    </button>
                    <button
                      type="button"
                      className={outcomeKey(event) === 'closed' ? 'primary' : ''}
                      disabled={saving}
                      onClick={() => void markOutcome(event, 'occurred', 'closed')}
                    >
                      Fechou
                    </button>
                    <button
                      type="button"
                      className={outcomeKey(event) === 'lost' ? 'primary' : ''}
                      disabled={saving}
                      onClick={() => void markOutcome(event, 'occurred', 'lost')}
                    >
                      Perdeu
                    </button>
                  </div>
                ) : null}
              </div>
              {event.source === 'mends' && event.status === 'confirmed' ? (
                <div className="agenda-day-nav">
                  <button type="button" onClick={() => openEvent(event)}>Editar</button>
                  <button type="button" className="danger" disabled={saving} onClick={() => void cancelEvent(event)}>Cancelar</button>
                </div>
              ) : null}
            </article>
          ))}
          <h2 style={{ marginTop: 20 }}>Horários livres</h2>
          <div className="agenda-slots">
            {slots.length === 0 ? <p className="agenda-empty">Nenhum horário livre neste dia.</p> : slots.map((slot) => (
              <span className="agenda-slot" key={`${slot.startsAt}-${slot.endsAt}`}>{slot.label || dayjs.utc(slot.startsAt).tz(zone).format('HH:mm')}</span>
            ))}
          </div>
        </section>

        <section className="agenda-panel">
          <div className="agenda-tabs">
            <button type="button" className={panel === 'event' ? 'primary' : ''} onClick={() => setPanel('event')}>Compromisso</button>
            <button type="button" className={panel === 'rules' ? 'primary' : ''} onClick={() => setPanel('rules')}>Parâmetros</button>
          </div>

          {panel === 'event' ? (
            <form className="agenda-form" onSubmit={(e) => { e.preventDefault(); void saveEvent(); }}>
              <label>
                Título
                <input value={title} onChange={(e) => setTitle(e.target.value)} required />
              </label>
              <div className="agenda-row">
                <label>
                  Início
                  <input type="time" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
                </label>
                <label>
                  Fim
                  <input type="time" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
                </label>
              </div>
              <label>
                Local
                <input value={location} onChange={(e) => setLocation(e.target.value)} />
              </label>
              <label>
                Descrição
                <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              </label>
              <div className="agenda-day-nav">
                <button type="submit" className="primary" disabled={saving}>{editing ? 'Salvar alterações' : 'Criar'}</button>
                {editing ? <button type="button" onClick={resetForm}>Novo</button> : null}
              </div>
            </form>
          ) : settings ? (
            <div className="agenda-form">
              <label>
                Fuso
                <TimezoneSelect value={settings.timeZoneId} onChange={(value) => setSettings({ ...settings, timeZoneId: value })} />
              </label>
              <div className="agenda-row">
                <label>
                  Passo do horário (min)
                  <input type="number" min={5} value={settings.slotStepMinutes} onChange={(e) => setSettings({ ...settings, slotStepMinutes: Number(e.target.value) })} />
                </label>
                <label>
                  Antecedência mínima (min)
                  <input type="number" min={0} value={settings.minNoticeMinutes} onChange={(e) => setSettings({ ...settings, minNoticeMinutes: Number(e.target.value) })} />
                </label>
              </div>
              <label>
                Janela de agendamento (dias)
                <input type="number" min={1} value={settings.bookingWindowDays} onChange={(e) => setSettings({ ...settings, bookingWindowDays: Number(e.target.value) })} />
              </label>
              <div className="agenda-hours">
                {hours.map((row, index) => (
                  <div className="agenda-hours-row" key={row.dayOfWeek}>
                    <input
                      type="checkbox"
                      checked={row.isOpen}
                      onChange={(e) => setHours(hours.map((item, i) => i === index ? { ...item, isOpen: e.target.checked } : item))}
                      aria-label={DAY_NAMES[row.dayOfWeek]}
                    />
                    <span>{DAY_NAMES[row.dayOfWeek]}</span>
                    <input
                      type="time"
                      value={row.openingTime.slice(0, 5)}
                      onChange={(e) => setHours(hours.map((item, i) => i === index ? { ...item, openingTime: e.target.value } : item))}
                    />
                    <input
                      type="time"
                      value={row.closingTime.slice(0, 5)}
                      onChange={(e) => setHours(hours.map((item, i) => i === index ? { ...item, closingTime: e.target.value } : item))}
                    />
                  </div>
                ))}
              </div>
              <button type="button" className="primary" disabled={saving} onClick={() => void saveRules()}>Salvar parâmetros</button>

              <h2>Tipos</h2>
              {types.map((type) => (
                <div className="agenda-event-meta" key={type.id}>
                  {type.name} · {type.durationMinutes} min · buffer {type.bufferBeforeMinutes}/{type.bufferAfterMinutes}
                  {type.isDefault ? ' · padrão' : ''}
                </div>
              ))}
              <div className="agenda-types">
                <label>
                  Nome
                  <input value={typeDraft.name} onChange={(e) => setTypeDraft({ ...typeDraft, name: e.target.value })} />
                </label>
                <div className="agenda-row">
                  <label>
                    Duração
                    <input type="number" min={5} value={typeDraft.durationMinutes} onChange={(e) => setTypeDraft({ ...typeDraft, durationMinutes: Number(e.target.value) })} />
                  </label>
                  <label>
                    Buffer depois
                    <input type="number" min={0} value={typeDraft.bufferAfterMinutes} onChange={(e) => setTypeDraft({ ...typeDraft, bufferAfterMinutes: Number(e.target.value) })} />
                  </label>
                </div>
                <button type="button" disabled={saving} onClick={() => void saveType()}>Adicionar tipo</button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
      <ToastContainer>
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => setToasts((current) => current.filter((item) => item.id !== toast.id))} />
        ))}
      </ToastContainer>
    </div>
  );
};

function fullWeek(rows: CalendarWorkingHours[]): CalendarWorkingHours[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const found = rows.find((row) => row.dayOfWeek === dayOfWeek);
    return found ?? {
      dayOfWeek,
      openingTime: '09:00',
      closingTime: '18:00',
      isOpen: false,
    };
  });
}

function commercialLabel(event: CalendarEvent): string | null {
  const occurrence = event.occurrence || 'pending';
  const outcome = event.outcome || 'none';
  if (occurrence === 'no_show') return 'Não compareceu';
  if (outcome === 'closed') return 'Fechou';
  if (outcome === 'lost') return 'Perdeu';
  if (outcome === 'no_decision') return 'Sem decisão';
  if (occurrence === 'occurred') return 'Ocorreu';
  return null;
}

function outcomeKey(event: CalendarEvent): string {
  const occurrence = event.occurrence || 'pending';
  const outcome = event.outcome || 'none';
  if (occurrence === 'no_show') return 'no_show';
  if (outcome === 'closed') return 'closed';
  if (outcome === 'lost') return 'lost';
  if (outcome === 'no_decision') return 'no_decision';
  return '';
}

function messageOf(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  return error instanceof Error ? error.message : 'Não foi possível falar com a agenda.';
}

export default AgendaPage;
